import api from '../../api/apiClient';

/**
 * Module dashboards (`DashboardsController`). One shape for every module, so a single screen
 * renders them all.
 *
 *   GET /api/dashboards            Home: one card per module the user can open
 *   GET /api/dashboards/{module}   that module's dashboard (403 when the user has no screen of it)
 */

export type KpiUnit = 'COUNT' | 'INR' | 'HOURS' | 'DAYS' | 'PERCENT';
export type KpiTone = 'GOOD' | 'WARN' | 'BAD' | 'NEUTRAL';
export type ChartKind = 'BAR' | 'LINE' | 'DONUT' | 'STACKED';

export interface Kpi {
  key: string;
  label: string;
  value: number | null;
  unit: KpiUnit | null;
  hint: string | null;
  tone: KpiTone | null;
  /** Admin-panel path; shown as a link only when the user can open it. */
  link: string | null;
}

export interface ChartPoint {
  label: string;
  value: number | null;
}

export interface ChartSeries {
  name: string;
  points: ChartPoint[];
}

export interface Chart {
  key: string;
  title: string;
  /** BAR: categories · LINE: over time · DONUT: parts of a whole · STACKED: one segment per series. */
  kind: ChartKind;
  unit: KpiUnit | null;
  series: ChartSeries[];
  link: string | null;
}

export interface TableColumn {
  key: string;
  label: string;
  unit: KpiUnit | null;
}

/** Column key → value; an optional `_link` gives the row's admin-panel path. */
export type TableRow = Record<string, unknown> & { _link?: string | null };

export interface DashTable {
  key: string;
  title: string;
  columns: TableColumn[];
  rows: TableRow[];
  emptyText: string | null;
  link: string | null;
}

export interface Dashboard {
  module: string;
  title: string;
  subtitle: string | null;
  generatedAt: string | null;
  kpis: Kpi[];
  charts: Chart[];
  tables: DashTable[];
}

export interface ModuleCard {
  module: string;
  label: string;
  /** Iconify name. */
  icon: string | null;
  link: string;
  kpis: Kpi[];
}

const payload = async <T>(promise: Promise<{ data: any }>): Promise<T> => (await promise).data?.data as T;

/** Home: a card per module the signed-in user can open. */
export function getModuleCards(): Promise<ModuleCard[]> {
  return payload<ModuleCard[]>(api.get('/api/dashboards')).then((d) => d ?? []);
}

/** One module's dashboard. Rejects with `response.status` 403 when the user has no screen of it. */
export function getDashboard(module: string): Promise<Dashboard> {
  return payload<Dashboard>(api.get(`/api/dashboards/${encodeURIComponent(module)}`));
}
